import { CSSProperties } from "react";

interface ParagraphProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
  className?: string;
  id?: string;
  style?: CSSProperties;
  role?: string;
  ariaLabelledBy?: string;
}

export const Paragraph = ({ children, ...rest }: ParagraphProps) => {
  return <p {...rest}>{children}</p>;
};

/**
 * A semantic paragraph component that wraps content in a p element.
 * Extends all standard HTML paragraph attributes while providing a consistent
 * interface for text content. Maintains accessibility and semantic structure
 * for screen readers and other assistive technologies.
 *
 * @component
 * @param {React.ReactNode} children - The text or elements to be displayed within the paragraph
 * @param {string} [className] - Additional CSS classes to apply to the paragraph
 * @param {string} [id] - Unique identifier for the paragraph element
 * @param {CSSProperties} [style] - Inline styles to apply to the paragraph
 * @param {string} [role] - ARIA role attribute for accessibility customization
 * @param {string} [ariaLabelledBy] - References the ID of an element that labels this paragraph
 *
 * @example
 * // Basic usage
 * <Paragraph>This is a simple paragraph of text.</Paragraph>
 *
 * @example
 * // With styling and classes
 * <Paragraph
 *   className="intro-text"
 *   style={{ fontSize: '18px', lineHeight: '1.6' }}
 * >
 *   This is an introductory paragraph with custom styling.
 * </Paragraph>
 *
 * @example
 * // With accessibility attributes
 * <Paragraph
 *   id="description"
 *   role="note"
 *   ariaLabelledBy="section-title"
 * >
 *   This paragraph provides additional context for the section above.
 * </Paragraph>
 *
 * @example
 * // With mixed content
 * <Paragraph className="highlight">
 *   This paragraph contains <strong>bold text</strong> and a
 *   <a href="/link">clickable link</a>.
 * </Paragraph>
 */
